import React, { useState, useEffect } from 'react';
import { Box, Container, Paper, Tabs, Tab, Typography, CircularProgress, Alert } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import ClientManagement from '../admin/ClientManagement';
import DepartmentManagement from '../admin/DepartmentManagement';
import TeamManagement from '../admin/TeamManagement';
import UserManagement from '../admin/UserManagement';
import HierarchyView from '../admin/HierarchyView';
import { getCurrentUser, getAllUsers } from '../api/services/userService/User';
import { getAllClients, getClientById } from '../api/services/userService/Client';
import { getAllDepartments, getDepartmentById } from '../api/services/userService/Department';
import { getAllTeams, getTeamById } from '../api/services/userService/Team';
import { getNodes, transformNodeToDisplayFormat } from '../api/services/computeService/NodeService';
import { getRoleValue } from '../../utils/roleUtils';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

export default function AdminPage() {
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [sharedData, setSharedData] = useState({
    clients: [],
    departments: [],
    teams: [],
    users: [],
    nodes: []
  });
  const [dataLoading, setDataLoading] = useState(false);
  const { keycloak } = useKeycloak();

  const fetchCurrentUser = async () => {
    try {
      await keycloak.updateToken(30);
      const response = await getCurrentUser(keycloak.token);
      
      if (response.success) {
        setCurrentUser(response.user);
        
        const permissions = {
          isSuperAdmin: response.user.superAdmin,
          clientIds: [],
          departmentIds: [],
          teamIds: [],
          clientRoles: {},
          departmentRoles: {},
          teamRoles: {}
        };

        if (response.user.client && response.user.client.client) {
          permissions.clientIds.push(response.user.client.client.id);
          permissions.clientRoles[response.user.client.client.id] = getRoleValue(response.user.client.client, 'client');
        }

        if (response.user.departments && Array.isArray(response.user.departments)) {
          response.user.departments.forEach(dept => {
            if (dept.department) {
              permissions.departmentIds.push(dept.department.id);
              permissions.departmentRoles[dept.department.id] = getRoleValue(dept.department, 'department');
            }
          });
        }

        if (response.user.teams && Array.isArray(response.user.teams)) {
          response.user.teams.forEach(team => {
            if (team.team) {
              permissions.teamIds.push(team.team.id);
              permissions.teamRoles[team.team.id] = getRoleValue(team.team, 'team');
            }
          });
        }

        setUserPermissions(permissions);
        return permissions;
      } else {
        setError(response.error || 'Failed to fetch user data');
        return null;
      }
    } catch (err) {
      console.error('Error fetching current user:', err);
      setError(err.message || 'Failed to fetch user data');
      return null;
    }
  };

  const fetchAllData = async (permissions) => {
    setDataLoading(true);
    try {
      await keycloak.updateToken(30);
      
      let clientsData = [];
      let departmentsData = [];
      let teamsData = [];
      let usersData = [];
      let nodesData = [];

      if (permissions?.isSuperAdmin) {
        const [clientsRes, departmentsRes, teamsRes, usersRes, nodesRes] = await Promise.all([
          getAllClients({
            pagination: { page: 1, pageSize: 1000 },
            sorting: { sort_by: 'SORT_BY_NAME', order: 'SORT_ORDER_ASCENDING' }
          }, keycloak.token),

          getAllDepartments({
            pagination: { page: 1, pageSize: 2000 },
            sorting: { sort_by: 'SORT_BY_NAME', order: 'SORT_ORDER_ASCENDING' }
          }, keycloak.token),

          getAllTeams({
            pagination: { page: 1, pageSize: 5000 },
            sorting: { sort_by: 'SORT_BY_NAME', order: 'SORT_ORDER_ASCENDING' }
          }, keycloak.token),

          getAllUsers({
            pagination: { page: 1, pageSize: 10000 },
            sorting: { sort_by: 'SORT_BY_NAME', order: 'SORT_ORDER_ASCENDING' }
          }, keycloak.token),

          // Load all nodes
          (async () => {
            try {
              const response = await getNodes({
                paginationParams: { page: 0, page_size: 1000 },
                token: keycloak.token
              });

              if (response.success && response.data?.nodes) {
                const transformedNodes = response.data.nodes.map(node =>
                  transformNodeToDisplayFormat(node) || node
                ).filter(node => node !== null);
                return { success: true, nodes: transformedNodes };
              }
              return { success: false, nodes: [] };
            } catch (error) {
              console.error('Error loading nodes:', error);
              return { success: false, nodes: [] };
            }
          })()
        ]);

        if (clientsRes.success) clientsData = clientsRes.clients || [];
        if (departmentsRes.success) departmentsData = departmentsRes.departments || [];
        if (teamsRes.success) teamsData = teamsRes.teams || [];
        if (usersRes.success) usersData = usersRes.users || [];
        if (nodesRes.success) nodesData = nodesRes.nodes || [];
      } else {
        const currentUserResponse = await getCurrentUser(keycloak.token);
        
        if (currentUserResponse.success) {
          const userData = currentUserResponse.user;
          
          if (userData.client) {
            const clientResponse = await getClientById(userData.client.client.id, keycloak.token);
            if (clientResponse.success) {
              clientsData = [clientResponse.client];
            }
          }

          if (userData.departments && userData.departments.length > 0) {
            const departmentPromises = userData.departments.map(deptData => 
              getDepartmentById(deptData.department.id, keycloak.token)
            );
            const departmentResponses = await Promise.all(departmentPromises);
            
            departmentResponses.forEach((response) => {
              if (response.success) {
                const department = response.department;
                department.clientId = userData.client ? userData.client.client.id : null;
                departmentsData.push(department);
              }
            });
          }

          if (userData.teams && userData.teams.length > 0) {
            const teamPromises = userData.teams.map(teamData => 
              getTeamById(teamData.team.id, keycloak.token)
            );
            const teamResponses = await Promise.all(teamPromises);
            
            teamResponses.forEach((response, index) => {
              if (response.success) {
                const team = response.team;
                const teamData = userData.teams[index];
                team.departmentId = teamData.team.departmentId;
                teamsData.push(team);
              }
            });
          }

          if (userData.client && userData.client.client) {
            const clientId = userData.client.client.id;
            const clientName = userData.client.client.name;
            const usersResponse = await getAllUsers({
              client_id: clientId,
              pagination: { page: 1, pageSize: 10000 },
              sorting: { sort_by: 'SORT_BY_NAME', order: 'SORT_ORDER_ASCENDING' }
            }, keycloak.token);

            if (usersResponse.success && usersResponse.users) {
              usersData = usersResponse.users.map(user => {
                const enrichedUser = { ...user };
                
                if (!enrichedUser.clients || enrichedUser.clients.length === 0) {
                  enrichedUser.clients = [{ 
                    id: clientId, 
                    name: clientName || 'Unknown Client', 
                    role: user.superAdmin ? 'admin' : 'member' 
                  }];
                  enrichedUser.clientIds = [clientId];
                  enrichedUser.clientId = clientId;
                }
                
                if (!enrichedUser.departments) {
                  enrichedUser.departments = [];
                  enrichedUser.departmentIds = [];
                }
                
                if (!enrichedUser.teams) {
                  enrichedUser.teams = [];
                  enrichedUser.teamIds = [];
                }
                
                return enrichedUser;
              });
            }
          }
        }
      }

      // Load nodes for all users
      try {
        const nodesResponse = await getNodes({
          paginationParams: { page: 0, page_size: 1000 },
          token: keycloak.token
        });

        if (nodesResponse.success && nodesResponse.data?.nodes) {
          const transformedNodes = nodesResponse.data.nodes.map(node =>
            transformNodeToDisplayFormat(node) || node
          ).filter(node => node !== null);
          nodesData = transformedNodes;
        }
      } catch (nodeError) {
        console.error('Error loading nodes:', nodeError);
        nodesData = [];
      }

      setSharedData({
        clients: clientsData,
        departments: departmentsData,
        teams: teamsData,
        users: usersData,
        nodes: nodesData
      });
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setError(err.message || 'Failed to fetch admin data');
    } finally {
      setDataLoading(false);
    }
  };

  const initializeAdminPage = async () => {
    setLoading(true);
    const permissions = await fetchCurrentUser();
    if (permissions) {
      await fetchAllData(permissions);
    }
    setLoading(false);
  };

  const handleRefreshData = async () => {
    if (userPermissions) {
      await fetchAllData(userPermissions);
    }
  };

  useEffect(() => {
    if (keycloak.token) {
      initializeAdminPage();
    }
  }, [keycloak.token]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const getAvailableTabs = () => {
    if (!userPermissions) return [];
    
    const tabs = [];
    
    if (userPermissions.isSuperAdmin || 
        userPermissions.clientIds.length > 0 || 
        userPermissions.departmentIds.length > 0 || 
        userPermissions.teamIds.length > 0) {
      tabs.push({ label: "Hierarchy", index: 0 });
    }
    
    if (userPermissions.isSuperAdmin || userPermissions.clientIds.length > 0) {
      tabs.push({ label: "Clients", index: tabs.length });
    }
    
    if (userPermissions.isSuperAdmin || 
        userPermissions.clientIds.length > 0 || 
        userPermissions.departmentIds.length > 0) {
      tabs.push({ label: "Departments", index: tabs.length });
    }
    
    if (userPermissions.isSuperAdmin ||
        userPermissions.departmentIds.length > 0 ||
        userPermissions.teamIds.length > 0) {
      tabs.push({ label: "Teams", index: tabs.length });
    }

    tabs.push({ label: "Users", index: tabs.length });
    
    return tabs;
  };

  const getTabComponent = (tabLabel, index) => {
    switch (tabLabel) {
      case "Hierarchy":
        return <HierarchyView 
          key="hierarchy-view" 
          userPermissions={userPermissions}
          sharedData={sharedData}
          onRefresh={handleRefreshData}
        />;
      case "Clients":
        return <ClientManagement 
          key="client-management" 
          userPermissions={userPermissions}
          sharedData={sharedData}
          onRefresh={handleRefreshData}
        />;
      case "Departments":
        return <DepartmentManagement 
          key="department-management" 
          userPermissions={userPermissions}
          sharedData={sharedData}
          onRefresh={handleRefreshData}
        />;
      case "Teams":
        return <TeamManagement 
          key="team-management" 
          userPermissions={userPermissions}
          sharedData={sharedData}
          onRefresh={handleRefreshData}
        />;
      case "Users":
        return <UserManagement 
          key="user-management" 
          userPermissions={userPermissions}
          sharedData={sharedData}
          onRefresh={handleRefreshData}
        />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const availableTabs = getAvailableTabs();

  return (
    <Container maxWidth="100%" sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: { xs: 2, sm: 3 } }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Tenant Dashboard
        </Typography>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabIndex} 
            onChange={handleTabChange} 
            aria-label="admin dashboard tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {availableTabs.map((tab, idx) => (
              <Tab key={tab.label} label={tab.label} {...a11yProps(idx)} />
            ))}
          </Tabs>
        </Box>

        {availableTabs.map((tab, idx) => (
          <TabPanel key={tab.label} value={tabIndex} index={idx}>
            {getTabComponent(tab.label, idx)}
          </TabPanel>
        ))}
      </Paper>
    </Container>
  );
} 